import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  IconButton,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  FlightTakeoff,
  FlightLand,
  Radar,
  Shield,
  Lock,
  Bolt,
  Warning,
  Cancel,
  Sensors as SensorsIcon,
  Timeline,
  Map as MapIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

import * as df from "date-fns";
import L from "leaflet";
import AtoTimeline, { TimelineGroup } from "../components/AtoTimeline";
import SidePanels from "../components/SidePanels";
import { useAuth } from "../auth/AuthContext";
import { apiFetch } from "../api";
import { useRealtime } from "../realtime";

// ── Types ─────────────────────────────────────────────────────────

interface MissionDetail {
  _id: string;
  callsign: string;
  classification: string;
  locked: boolean;
  hold: boolean;
  holdType?: string;
  canx: boolean;
  frequency: string;
  armed: boolean;
  operatingArea: string;
  ato_day: string;
  ato_mission_number: string;
  int_prime: string;
  int_secondary: string;
  country: string;
  COM_guidance?: string;
  primaryTaskingName?: string;
  lostLinkStatus?: boolean;
  refuelRequired?: boolean;
  mirc_channels?: string[];
  platform?: {
    name: string;
    designator: string;
    fullName: string;
    color?: string;
    int_disciplines: string[];
  };
  sensors?: Array<{
    sensor_name?: string;
    sensor_short_name?: string;
    int_discipline?: string;
  }>;
  flying_unit?: { name?: string; service?: string; country?: string };
  com_unit?: { name?: string; auth_role?: string };
  ped_units?: Array<string | { name?: string; id?: string }>;
  events?: {
    takeoff?: { scheduled?: string | null; actual?: string | null; base?: string | null; time?: string; };
    landing?: { scheduled?: string | null; actual?: string | null; base?: string | null; time?: string; };
  };
  _schedule: {
    launchBase: string;
    landingBase?: string;
    onStation: string | null;
    offStation: string | null;
    takeoff?: string | null;
    landing?: string | null;
    takeoffActual?: string | null;
    landingActual?: string | null;
  };
  _userTags?: string[];
  _taskings: Array<{
    _id: string;
    type: string;
    collection_type: string;
    primary_int_discipline: string;
    secondary_int_discipline: string;
    operation: string;
    priority: number;
    operatingArea: string;
    allocated_area: string;
    supported_unit?: string;
    collection_periods?: {
      planned?: { start?: string; stop?: string; end?: string };
    };
  }>;
  _collectionDecks?: Array<{
    _id: string;
    type: string;
    name?: string;
    primary_int_discipline?: string;
    secondary_int_discipline?: string;
    priority?: number;
    operation?: string;
    allocated_area?: string;
    supported_unit?: string;
    collection_requirements: Array<{
      _id: string;
      int_discipline?: string;
      secondary?: boolean;
      priority?: string;
      type?: string;
      operation?: string;
      supported_unit?: string;
      country?: string;
      region?: string;
      sub_region?: string;
      jipcl_sensor?: string;
      targets?: Array<{
        _id: string;
        name?: string;
        target_id?: string;
        status?: string;
      }>;
      exploitation_requirement?: {
        eei?: string;
        amplification?: string;
        reportingCriteria?: string;
        dissemination?: string;
      };
    }>;
  }>;
}

interface ListMission {
  _id: string;
  callsign: string;
  operatingArea: string;
  ato_day: string;
  int_prime: string;
  int_secondary: string;
  platform?: { name: string; designator: string; fullName: string; color?: string };
  sensors?: Array<{ sensor_name?: string; sensor_short_name?: string; int_discipline?: string }>;
  armed: boolean;
  locked: boolean;
  hold: boolean;
  holdType?: string;
  canx: boolean;
  lastChangeType?: string | null;
  launchBase: string;
  landingBase?: string;
  onStation: string | null;
  offStation: string | null;
  takeoff?: string | null;
  landing?: string | null;
  primaryTaskingName?: string;
  events?: MissionDetail["events"];
}

interface GroupedMissions {
  missions: ListMission[];
  platforms?: Record<string, ListMission[]>;
  ints?: Record<string, ListMission[]>;
  sensors?: Record<string, ListMission[]>;
}

interface DbPlatform {
  _id: string;
  name: string;
  designator: string;
  fullName?: string;
  color?: string;
  int_disciplines?: string[];
}

interface DbBase {
  _id: string;
  base_name: string;
  base_icao: string;
  base_coords: string;
}

interface DbOpArea {
  _id?: string;
  name?: string;
  short_name?: string;
  location?: { lat?: string; lng?: string; zoom?: string };
}

// ── Constants ────────────────────────────────────────────────────

interface ChangeAction {
  label: string;
  slug: string;
  fields?: Array<{ name: string; label: string; type: "datetime" | "text" }>;
  noReason?: boolean;
}

// The IDEX change menu — slugs match POST /api/mission/:id/change/<slug>
const HOLD_ACTION: ChangeAction = { label: "Hold", slug: "hold" };
const DELAY_ACTION: ChangeAction = {
  label: "Delay", slug: "delay",
  fields: [
    { name: "newTakeoff", label: "New Takeoff (Z)", type: "datetime" },
    { name: "newLanding", label: "New Landing (Z)", type: "datetime" },
  ],
};
const CANX_ACTION: ChangeAction = { label: "CANX", slug: "cancel" };
const MORE_ACTIONS: ChangeAction[] = [
  { label: "Extension", slug: "extension", fields: [
    { name: "newLanding", label: "New Landing (Z)", type: "datetime" },
    { name: "newOffsta", label: "New Off Station (Z)", type: "datetime" },
  ] },
  { label: "Early RTB", slug: "early-return-to-base", fields: [
    { name: "newLanding", label: "New Landing (Z)", type: "datetime" },
    { name: "newOffsta", label: "New Off Station (Z)", type: "datetime" },
  ] },
  { label: "Early On-Station", slug: "early-on-station", fields: [
    { name: "newOnsta", label: "New On Station (Z)", type: "datetime" },
  ] },
  { label: "Vul Shift", slug: "vul-shift", fields: [
    { name: "newOnsta", label: "New On Station (Z)", type: "datetime" },
    { name: "newOffsta", label: "New Off Station (Z)", type: "datetime" },
  ] },
  { label: "Divert", slug: "divert", fields: [
    { name: "newToBase", label: "New Landing Base (ICAO)", type: "text" },
  ] },
  { label: "New Callsign", slug: "new-callsign", noReason: true, fields: [
    { name: "newCallsign", label: "New Callsign", type: "text" },
  ] },
  { label: "Lost Link", slug: "lost-link" },
  { label: "Downed Asset", slug: "downed-asset" },
  { label: "Cross Cue", slug: "cross-cue" },
  { label: "Retask", slug: "retask" },
  { label: "Reaction", slug: "reaction" },
  { label: "Strike Support", slug: "strike" },
  { label: "Alt Tasking", slug: "alt-tasking" },
  { label: "Minor Variation", slug: "minor-variation" },
  { label: "Hybrid Management", slug: "hybrid-management" },
  { label: "Ground Alert Launch", slug: "launch" },
];

// Self-hosted basemap (BitBot MapProxy, same source as nuptialflight/orrery).
// The three IDEX themes are CSS filters over the one tile source so nothing
// depends on a third-party tile CDN.
const BITBOT_TILES = "https://maps.bitbot.com.au/tiles/1.0.0/backdrop/EPSG3857/{z}/{x}/{y}.png";
const MAP_THEME_FILTER: Record<"dark" | "medium" | "light", string> = {
  dark: "invert(1) brightness(0.85) contrast(1.1)",
  medium: "brightness(0.75) saturate(0.6)",
  light: "none",
};
function makeTileLayer(theme: "dark" | "medium" | "light"): L.TileLayer {
  return L.tileLayer(BITBOT_TILES, {
    maxZoom: 19,
    className: `basemap-${theme}`,
    attribution: '&copy; OpenStreetMap contributors &middot; tiles by BitBot Software',
  });
}

const INT_COLORS: Record<string, string> = {
  FMV: "#00e676",
  GEOINT: "#00b8ff",
  SIGINT: "#ff9100",
  MTI: "#ff1744",
  MASINT: "#d500f9",
};

function dmsToLatLng(dms: string): [number, number] | null {

  if (!dms) return null;
  const m = dms.match(/^(\d\d)(\d\d)(\d\d)([NS])\s*(\d\d\d)(\d\d)(\d\d)([EW])$/);
  if (!m) return null;
  let lat = parseInt(m[1]) + parseInt(m[2]) / 60 + parseInt(m[3]) / 3600;
  let lng = parseInt(m[5]) + parseInt(m[6]) / 60 + parseInt(m[7]) / 3600;
  if (m[4] === "S") lat = -lat;
  if (m[8] === "W") lng = -lng;
  return [lat, lng];
}

/**
 * IDEX-style ATO day header, e.g. "ATO 24-128 · 07 MAY 2024 · D-836":
 * two-digit year + Julian day of year, the date, and days relative to today
 * (past dates negative, "D Day" for today) — matching the reference site.
 */
function atoHeader(atoDate: string): string | null {
  if (!atoDate) return null;
  try {
    const d = df.parseISO(atoDate);
    const julian = `${df.format(d, "yy")}-${String(df.getDayOfYear(d)).padStart(3, "0")}`;
    const days = df.differenceInCalendarDays(d, new Date());
    const dLabel = days === 0 ? "D Day" : days > 0 ? `D+${days}` : `D${days}`;
    return `ATO ${julian} · ${df.format(d, "dd MMM yyyy").toUpperCase()} · ${dLabel}`;
  } catch {
    return null;
  }
}

function fmtZ(iso: string | null | undefined): string {
  if (!iso) return "--";
  try {
    return df.format(df.parseISO(iso), "HH:mm'Z'");
  } catch {
    return "--";
  }
}

function fmtZLong(iso: string | null | undefined): string {
  if (!iso) return "--";
  try {
    return df.format(df.parseISO(iso), "dd MMM HH:mm'Z'");
  } catch {
    return "--";
  }
}

function periodStart(t: MissionDetail["_taskings"][0]): string | null {
  return t.collection_periods?.planned?.start || null;
}
function periodEnd(t: MissionDetail["_taskings"][0]): string | null {
  const p = t.collection_periods?.planned;
  return p?.stop || p?.end || null;
}

// ── Component ────────────────────────────────────────────────────


export default function Main() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const missionId = searchParams.get("mission");
  const operatingArea = searchParams.get("operatingArea") || "";
  const atoDate = searchParams.get("atoDate") || "";
  const group = searchParams.get("group") || "PLATFORM";
  const view = searchParams.get("view") || "TIMELINE";
  const sortBy = (searchParams.get("sort") === "CALLSIGN" ? "CALLSIGN" : "TAKEOFF") as "TAKEOFF" | "CALLSIGN";
  const startHour = Math.min(23, Math.max(0, parseInt(searchParams.get("startZ") || "0", 10) || 0));
  const [search, setSearch] = useState("");
  const { user } = useAuth();

  // ── Mission change workflow state (config-driven dialog) ──
  const [changeDialog, setChangeDialog] = useState<ChangeAction | null>(null);
  const [changeReason, setChangeReason] = useState("WX");
  const [changeJustification, setChangeJustification] = useState("");
  const [changeFields, setChangeFields] = useState<Record<string, string>>({});
  const [changeBusy, setChangeBusy] = useState(false);
  const [changeErr, setChangeErr] = useState<string | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [changeLog, setChangeLog] = useState<Array<{ type: string; reason: string; justification: string; author: string; at: string }>>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Realtime: any mission:* broadcast triggers one debounced refetch (~500ms)
  const realtimeRefreshTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { connected: realtimeConnected } = useRealtime(() => {
    if (realtimeRefreshTimer.current) clearTimeout(realtimeRefreshTimer.current);
    realtimeRefreshTimer.current = setTimeout(() => setRefreshKey((k) => k + 1), 500);
  });
  useEffect(() => () => {
    if (realtimeRefreshTimer.current) clearTimeout(realtimeRefreshTimer.current);
  }, []);

  const openChange = (action: ChangeAction) => {
    setChangeDialog(action);
    setChangeFields({});
    setChangeErr(null);
    setMoreAnchor(null);
  };

  const submitChange = async () => {
    if (!missionId || !changeDialog) return;
    setChangeBusy(true);
    setChangeErr(null);
    const body: Record<string, string> = {};
    if (!changeDialog.noReason) body.reason = changeReason;
    if (changeJustification) body.justification = changeJustification;
    for (const f of changeDialog.fields || []) {
      const v = changeFields[f.name];
      if (v) body[f.name] = f.type === "datetime" ? new Date(v).toISOString() : v;
    }
    const path = changeDialog.slug === "relaunch"
      ? `/api/mission/${missionId}/relaunch`
      : `/api/mission/${missionId}/change/${changeDialog.slug}`;
    const res = await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
    const payload = await res.json().catch(() => ({}));
    setChangeBusy(false);
    if (!res.ok) {
      setChangeErr(payload.error || `HTTP ${res.status}`);
      return;
    }
    setChangeDialog(null);
    setChangeJustification("");
    setRefreshKey((k) => k + 1);
  };

  const quickChange = async (slug: string) => {
    if (!missionId) return;
    const path = slug === "relaunch" ? `/api/mission/${missionId}/relaunch` : `/api/mission/${missionId}/change/${slug}`;
    const res = await apiFetch(path, { method: "POST", body: JSON.stringify({}) });
    if (res.ok) setRefreshKey((k) => k + 1);
  };

  const [mission, setMission] = useState<MissionDetail | null>(null);
  const [missionErr, setMissionErr] = useState(false);
  const [groupedData, setGroupedData] = useState<GroupedMissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [dbPlatforms, setDbPlatforms] = useState<DbPlatform[]>([]);
  const [dbBases, setDbBases] = useState<DbBase[]>([]);
  const [opAreas, setOpAreas] = useState<DbOpArea[]>([]);
  const [operatingAreasList, setOperatingAreasList] = useState<string[]>([
    "AUS", "AFG", "IRAN", "IRAQ", "YEMEN", "AFRICOM", "NORTHCOM", "PACOM", "EUCOM", "SOUTHCOM", "ALLOTHER",
  ]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedPlatformInGroup, setSelectedPlatformInGroup] = useState<string | null>(null);
  const mapRef = React.useRef<L.Map | null>(null);
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<L.LayerGroup | null>(null);
  const tileLayerRef = React.useRef<L.TileLayer | null>(null);

  // IDEX map settings: Dark / Medium / Light basemap themes
  const mapTheme = (["medium", "light"].includes(searchParams.get("mapTheme") || "")
    ? searchParams.get("mapTheme")
    : "dark") as "dark" | "medium" | "light";

  // Swap basemap tiles when the theme changes (map itself persists)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    tileLayerRef.current = makeTileLayer(mapTheme).addTo(map);
  }, [mapTheme]);

  // Docked layout: timeline dock at the bottom + collapsible detail column, so the
  // map is never fully covered. Leaflet must re-measure when either resizes.
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [detailOpen, setDetailOpen] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [timelineOpen, detailOpen]);

  // ── IDEX map display options (SETTINGS panel) ──
  const [mapOpts, setMapOpts] = useState({
    bases: true,
    otherMissions: true,
    taskingRings: true,
    targets: true,
    sroBoundaries: false,
    rings60: false,
  });
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [sroAreas, setSroAreas] = useState<Array<{ sro_area?: string; boundary?: string[] }>>([]);
  const [availableLayers, setAvailableLayers] = useState<Array<{ _id: string; name: string }>>([]);
  const [activeLayers, setActiveLayers] = useState<string[]>([]);
  const layersGroupRef = React.useRef<L.LayerGroup | null>(null);
  const layerGeojsonCacheRef = React.useRef<Map<string, any>>(new Map());

  useEffect(() => {
    fetch("/api/sro-areas").then((r) => r.json()).then((d) => setSroAreas(Array.isArray(d) ? d : [])).catch(() => {});
    fetch("/api/layer-geojson").then((r) => r.json()).then((d) => setAvailableLayers(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Overlay layers (KMZ/KML converted to GeoJSON server-side) live in their
  // own persistent group so redrawing mission overlays doesn't clear them.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!layersGroupRef.current) layersGroupRef.current = L.layerGroup().addTo(map);
    const group = layersGroupRef.current;
    group.clearLayers();
    let cancelled = false;
    (async () => {
      for (const id of activeLayers) {
        let gj = layerGeojsonCacheRef.current.get(id);
        if (!gj) {
          try {
            const r = await fetch(`/api/layer-geojson/${id}`);
            if (r.ok) {
              gj = (await r.json()).geojson;
              layerGeojsonCacheRef.current.set(id, gj);
            }
          } catch { /* leave layer off on fetch failure */ }
        }
        if (gj && !cancelled) {
          L.geoJSON(gj, {
            style: { color: "#00b8ff", weight: 1, opacity: 0.7, fillOpacity: 0.04 },
            pointToLayer: (_f, ll) => L.circleMarker(ll, { radius: 3, color: "#00b8ff", weight: 1 }),
          }).addTo(group);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeLayers]);

  useEffect(() => {
    fetch("/api/platforms").then((r) => r.json()).then((d) => setDbPlatforms(Array.isArray(d) ? d : [])).catch(() => {});
    fetch("/api/bases").then((r) => r.json()).then((d) => setDbBases(Array.isArray(d) ? d : [])).catch(() => {});
    fetch("/api/operatingAreas")
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d)) {
          setOpAreas(d);
          const fetchedNames = d
            .map((a: DbOpArea) => (a.short_name || a.name || "").toUpperCase())
            .filter(Boolean);
          setOperatingAreasList((prev) => Array.from(new Set([...prev, ...fetchedNames])).sort());
        }
      })
      .catch(() => {});
  }, []);

    const getPlatformColor = (designatorOrName: string): string => {
    const p = dbPlatforms.find(
      (pl) => pl.designator === designatorOrName || pl.name === designatorOrName
    );
    return p?.color || "#c42ebe";
  };

  const groupColor = (kind: string, key: string, list: ListMission[]): string => {
    if (kind === "PLATFORM") return getPlatformColor(list[0]?.platform?.designator || list[0]?.platform?.name || "");
    if (kind === "INT") return INT_COLORS[key] || "#c42ebe";
    if (kind === "SENSOR") return "#00b8ff";
    return "#c42ebe";
  };

  const basesByIcao = useMemo(() => {
    const m: Record<string, DbBase> = {};
    dbBases.forEach((b) => {
      if (b.base_icao) m[b.base_icao] = b;
    });
    return m;
  }, [dbBases]);

  const selectedOpArea = useMemo(() => {
    if (!operatingArea) return null;
    const key = operatingArea.toUpperCase();
    return (
      opAreas.find(
        (a) =>
          (a.short_name || "").toUpperCase() === key ||
          (a.name || "").toUpperCase() === key
      ) || null
    );
  }, [opAreas, operatingArea]);

  // Fetch grouped missions
  useEffect(() => {
    const fetchGrouped = async () => {
      const params = new URLSearchParams();
      if (operatingArea) params.set("operatingArea", operatingArea);
      if (atoDate) params.set("atoDate", atoDate);
      if (group) params.set("group", group);
      const res = await fetch(`/api/main/missions?${params.toString()}`);
      if (res.ok) setGroupedData(await res.json());
    };
    fetchGrouped();
  }, [operatingArea, atoDate, group, refreshKey]);

  // Fetch mission detail
  useEffect(() => {
    if (!missionId) {
      setMission(null);
      setMissionErr(false);
      return;
    }
    setMissionErr(false);
    setLoading(true);
    fetch(`/api/main/mission/${missionId}`)
      .then(async (res) => {
        if (res.ok) setMission(await res.json());
        else {
          setMission(null);
          setMissionErr(true);
        }
      })
      .catch(() => {
        setMission(null);
        setMissionErr(true);
      })
      .finally(() => setLoading(false));
  }, [missionId, refreshKey]);

  // Fetch the mission's change log for the detail panel
  useEffect(() => {
    if (!missionId) {
      setChangeLog([]);
      return;
    }
    fetch(`/api/audit-log/${missionId}`)
      .then(async (res) => setChangeLog(res.ok ? await res.json() : []))
      .catch(() => setChangeLog([]));
  }, [missionId, refreshKey]);

  const setParam = (key: string, value: string | null, clearMission = false) => {
    setSelectedGroup(null);
    setSelectedPlatformInGroup(null);
    const s = new URLSearchParams(searchParams);
    if (value) s.set(key, value);
    else s.delete(key);
    if (clearMission) s.delete("mission");
    navigate(`/main?${s.toString()}`);
  };

  // No ATO date in the URL: open on the latest day that actually has missions
  // rather than an empty map (IDEX opens on "today"; our data is historical).
  useEffect(() => {
    if (atoDate) return;
    let cancelled = false;
    fetch("/api/main/dates")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d?.latest) return;
        const s = new URLSearchParams(searchParams);
        s.set("atoDate", d.latest);
        navigate(`/main?${s.toString()}`, { replace: true });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [atoDate]);

  const selectMission = (id: string) => {
    const s = new URLSearchParams(searchParams);
    s.set("mission", id);
    setDetailOpen(true);
    navigate(`/main?${s.toString()}`);
  };

  // Init / update map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const isNew = !mapRef.current;
    if (isNew) {
      const newMap = L.map(mapContainerRef.current, {
        zoomControl: true,
        attributionControl: false,
      }).setView([-25, 134], 4);
      tileLayerRef.current = makeTileLayer(mapTheme).addTo(newMap);
      overlayRef.current = L.layerGroup().addTo(newMap);
      mapRef.current = newMap;
      setTimeout(() => newMap.invalidateSize(), 100);
    }
    const map = mapRef.current;
    if (!map || !overlayRef.current) return;

    overlayRef.current.clearLayers();
    const layer = overlayRef.current;

    // All bases as small dots
    if (mapOpts.bases) dbBases.forEach((b) => {
      const ll = dmsToLatLng(b.base_coords);
      if (!ll) return;
      L.circleMarker(ll, {
        radius: 3,
        color: "#555",
        fillColor: "#444",
        fillOpacity: 0.7,
        weight: 1,
      })
        .addTo(layer)
        .bindTooltip(`${b.base_name} (${b.base_icao})`, { direction: "top" });
    });

    // SRO area boundaries (real IDEX track polygons, "lat,lng" vertex strings)
    if (mapOpts.sroBoundaries) sroAreas.forEach((s) => {
      const pts = (s.boundary || [])
        .map((v) => {
          const [lat, lng] = String(v).split(",").map(parseFloat);
          return Number.isFinite(lat) && Number.isFinite(lng) ? ([lat, lng] as [number, number]) : null;
        })
        .filter((p): p is [number, number] => p !== null);
      if (pts.length < 3) return;
      L.polygon(pts, { color: "#ff9100", weight: 1.5, dashArray: "6 4", fillColor: "#ff9100", fillOpacity: 0.05 })
        .addTo(layer)
        .bindTooltip(s.sro_area || "SRO", { direction: "center" });
    });

        const listMissions = (groupedData?.missions || []).filter((m) => {
      if (!selectedGroup) return true;
      let inGroup = false;
      if (group === "PLATFORM") inGroup = (m.platform?.name || "Unknown") === selectedGroup;
      else if (group === "INT") inGroup = (m.int_prime || "Unknown") === selectedGroup;
      else if (group === "SENSOR") inGroup = (m.sensors || []).some((s) => (s.sensor_short_name || s.sensor_name || "") === selectedGroup);
      if (!inGroup) return false;
      if (selectedPlatformInGroup) return (m.platform?.name || "Unknown") === selectedPlatformInGroup;
      return true;
    });
    const bounds: L.LatLngExpression[] = [];

    // Other missions in filter
        listMissions.forEach((m) => {
      const icao = m.launchBase || m.events?.takeoff?.base || "";
      const base = basesByIcao[icao || ""];
      const ll = base ? dmsToLatLng(base.base_coords) : null;
      if (!ll) return;
      // Always frame the launch base of every mission in the filter
      bounds.push(ll);
      if (m._id !== missionId) {
        if (!mapOpts.otherMissions) return;
        const color = getPlatformColor(m.platform?.designator || m.platform?.name || "");
        L.circleMarker(ll, {
          radius: 6,
          color,
          fillColor: color,
          fillOpacity: 0.55,
          weight: 1,
        })
          .addTo(layer)
          .bindTooltip(`${m.callsign}`, { direction: "top" })
          .on("click", () => selectMission(m._id));
      } else {
        // Selected mission: also frame its (different) landing base if known
        const landingIcao = m.landingBase || m.events?.landing?.base;
        if (landingIcao && landingIcao !== icao) {
          const lb = basesByIcao[landingIcao];
          const lll = lb ? dmsToLatLng(lb.base_coords) : null;
          if (lll) bounds.push(lll);
        }
      }
    });
    const missionColor = getPlatformColor(mission?.platform?.designator || mission?.platform?.name || "");
    let focusLatLng: [number, number] | null = null;
    const missionBounds: L.LatLngExpression[] = [];
    let routeTakeoff: [number, number] | null = null;
    let routeLanding: [number, number] | null = null;

    if (mission) {
      const takeoffIcao =
        mission._schedule.launchBase ||
        mission.events?.takeoff?.base ||
        "";
      const landingIcao =
        mission._schedule.landingBase ||
        mission.events?.landing?.base ||
        takeoffIcao;
      const takeoffBase = basesByIcao[takeoffIcao];
      const landingBase = basesByIcao[landingIcao || ""];
      const takeoffLl = takeoffBase ? dmsToLatLng(takeoffBase.base_coords) : null;
      const landingLl = landingBase ? dmsToLatLng(landingBase.base_coords) : null;
      routeTakeoff = takeoffLl;
      routeLanding = landingLl;

      if (takeoffLl) {
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:16px;height:16px;border-radius:50%;background:${missionColor};border:2px solid #fff;box-shadow:0 0 8px ${missionColor};"></div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        L.marker(takeoffLl, { icon })
          .addTo(layer)
          .bindTooltip(`TO: ${mission.callsign} @ ${takeoffIcao}`, { direction: "top" });
        // framing bounds handled synchronously in the listMissions loop above
        focusLatLng = takeoffLl;
      }

      if (landingLl && landingIcao !== takeoffIcao) {
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:14px;height:14px;border-radius:3px;background:${missionColor};border:2px solid #fff;opacity:0.9;"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        });
        L.marker(landingLl, { icon })
          .addTo(layer)
          .bindTooltip(`LDG: ${mission.callsign} @ ${landingIcao}`, { direction: "top" });
        // framing bounds handled synchronously in the listMissions loop above
      }

      if (takeoffLl) missionBounds.push(takeoffLl);
      if (landingLl && landingIcao !== takeoffIcao) missionBounds.push(landingLl);

      // Tasking area rings near takeoff / OA center
      const ringCenter =
        takeoffLl ||
        (selectedOpArea?.location?.lat && selectedOpArea?.location?.lng
          ? ([
              parseFloat(selectedOpArea.location.lat),
              parseFloat(selectedOpArea.location.lng),
            ] as [number, number])
          : null);

      if (ringCenter && mapOpts.taskingRings && mission._taskings?.length) {
        mission._taskings.forEach((t, idx) => {
          const radius = 40000 + idx * 25000;
          L.circle(ringCenter, {
            radius,
            color: INT_COLORS[t.primary_int_discipline] || missionColor,
            fillColor: INT_COLORS[t.primary_int_discipline] || missionColor,
            fillOpacity: 0.06,
            weight: 1,
            dashArray: "4 4",
          })
            .addTo(layer)
            .bindTooltip(
              `${t.allocated_area || t.operatingArea} · ${t.primary_int_discipline || ""} · P${t.priority ?? ""}`,
              { direction: "top" }
            );
        });
      }
    }

      // Collection Deck / NIB targets - plot each target at its own DMS location.
      // Each target is stamped with a collection start time (from its tasking's
      // collection_periods.planned.start) so the route can be time-ordered,
      // falling back to geographic nearest-neighbour when no times are present.
      const targetPts: Array<{
        latlng: [number, number];
        name: string;
        int: string;
        deckType: string;
        start: string | null;
      }> = [];
      const seenTgt = new Set<string>();
      const collectTargets = (
        crs: any[] | undefined,
        deckType: string,
        startIso: string | null
      ) => {
        (crs || []).forEach((cr: any) => {
          const int = cr.int_discipline || cr.primary_int_discipline || "UNKNOWN";
          (cr.targets || []).forEach((t: any) => {
            const id = t._id || t.target_id || t.name;
            if (id && seenTgt.has(String(id))) return;
            if (id) seenTgt.add(String(id));
            const dms =
              t.location?.lookAtCoordinate ||
              (Array.isArray(t.location?.coordinates) ? t.location.coordinates[0] : null) ||
              t.location?.centerGeo;
            const ll = dms ? dmsToLatLng(dms) : null;
            if (!ll) return;
            targetPts.push({
              latlng: ll,
              name: t.name || t.target_id || "TGT",
              int,
              deckType,
              start: startIso || null,
            });
          });
        });
      };
      // NIB targets carry no tasking time.
      const nibAny = (mission as any)?.nib_task;
      if (nibAny?.collection_requirements) {
        collectTargets(nibAny.collection_requirements, "NIB", null);
      }
      // TASK targets get their time from the owning tasking's collection_periods.
      (mission?._taskings || []).forEach((t: any) => {
        const start = t.collection_periods?.planned?.start || null;
        collectTargets(
          t.collection_requirements,
          t.type || t.category || "TASK",
          start
        );
      });
      // Order targets into a visiting sequence for the mission route
      // (takeoff -> targets -> landing). IDEX supplies no explicit route/waypoint
      // order (flight_path/transit are empty). Prefer ordering by collection
      // start time; if times are absent/missing, fall back to geographic
      // nearest-neighbour from the takeoff base (with a geo tie-break inside a
      // shared time bucket).
      const distSq = (a: [number, number], b: [number, number]) =>
        (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
      const timed = targetPts.filter((tp) => tp.start && !isNaN(Date.parse(tp.start)));
      const ordered: typeof targetPts = [];
      if (timed.length > 0) {
        // Time-first: sort by start; nearest-neighbour within identical times.
        const byTime = [...timed].sort(
          (a, b) => Date.parse(a.start as string) - Date.parse(b.start as string)
        );
        let last: [number, number] | null = routeTakeoff;
        while (byTime.length > 0) {
          let bi = 0;
          if (last) {
            let best = Infinity;
            byTime.forEach((tp, i) => {
              const d = distSq(last as [number, number], tp.latlng);
              if (d < best) {
                best = d;
                bi = i;
              }
            });
          }
          const next = byTime.splice(bi, 1)[0];
          ordered.push(next);
          last = next.latlng;
        }
        // Targets with no usable time trail behind, geo-chained from the last point.
        const untimed = targetPts.filter(
          (tp) => !(tp.start && !isNaN(Date.parse(tp.start)))
        );
        let cursor = last;
        while (untimed.length > 0) {
          let bi = 0;
          if (cursor) {
            let best = Infinity;
            untimed.forEach((tp, i) => {
              const d = distSq(cursor as [number, number], tp.latlng);
              if (d < best) {
                best = d;
                bi = i;
              }
            });
          }
          const next = untimed.splice(bi, 1)[0];
          ordered.push(next);
          cursor = next.latlng;
        }
      } else {
        // Pure geographic ordering (no usable times anywhere).
        const remaining = [...targetPts];
        let cursor: [number, number] | null = routeTakeoff;
        while (remaining.length > 0) {
          let bi = 0;
          if (cursor) {
            let best = Infinity;
            remaining.forEach((tp, i) => {
              const d = distSq(cursor as [number, number], tp.latlng);
              if (d < best) {
                best = d;
                bi = i;
              }
            });
          }
          const next = remaining.splice(bi, 1)[0];
          ordered.push(next);
          cursor = next.latlng;
        }
      }
      if (mapOpts.targets) ordered.forEach((tp, i) => {
        const col = INT_COLORS[tp.int] || "#ff5cae";
        if (mapOpts.rings60) {
          // 60 NM range ring around each target (1 NM = 1852 m)
          L.circle(tp.latlng, { radius: 60 * 1852, color: col, weight: 1, dashArray: "2 6", fillOpacity: 0.02 }).addTo(layer);
        }
        L.circleMarker(tp.latlng, {
          radius: 6,
          color: "#fff",
          fillColor: col,
          fillOpacity: 0.9,
          weight: 1.5,
        })
          .addTo(layer)
          .bindTooltip(`#${i + 1} · TGT: ${tp.name} · ${tp.int} · ${tp.deckType}`, {
            direction: "top",
          });
        // Small numbered waypoint label
        L.marker(tp.latlng, {
          icon: L.divIcon({
            className: "",
            html: `<div style="font-size:9px;font-weight:700;color:#fff;text-shadow:0 0 3px #000,0 0 3px #000;transform:translate(8px,-14px);white-space:nowrap;">${i + 1}</div>`,
            iconSize: [0, 0],
          }),
          interactive: false,
        }).addTo(layer);
        missionBounds.push(tp.latlng);
      });
      // Route polyline: takeoff -> ordered targets -> landing
      const routePts: [number, number][] = [];
      if (routeTakeoff) routePts.push(routeTakeoff);
      ordered.forEach((tp) => routePts.push(tp.latlng));
      if (routeLanding) routePts.push(routeLanding);
      if (routePts.length >= 2) {
        L.polyline(routePts, {
          color: missionColor,
          weight: 2,
          opacity: 0.85,
          dashArray: "6 4",
        }).addTo(layer);
      }

    // View framing
    if (selectedGroup) {
      // A platform is selected — zoom in and focus the map on that platform's features
      if (bounds.length > 0) {
        // Dedupe so multiple missions from the same base count as a single point
        const distinct = Array.from(
          new Set(bounds.map((b) => JSON.stringify(b)))
        ).map((s) => JSON.parse(s) as L.LatLngExpression);
        const bb = L.latLngBounds(distinct);
        if (distinct.length > 1) {
          map.fitBounds(bb, { padding: [50, 50], maxZoom: selectedPlatformInGroup ? 9 : 8 });
        } else {
          // fitBounds is a no-op on a single point - zoom explicitly so the camera actually moves
          const c = bb.getCenter();
          map.setView([c.lat, c.lng], selectedPlatformInGroup ? 9 : 8);
        }
      } else if (selectedOpArea?.location?.lat && selectedOpArea?.location?.lng) {
        const lat = parseFloat(selectedOpArea.location.lat);
        const lng = parseFloat(selectedOpArea.location.lng);
        if (!isNaN(lat) && !isNaN(lng)) map.setView([lat, lng], selectedPlatformInGroup ? 7 : 6);
      }
    } else if (missionId && missionBounds.length > 0) {
      // A single mission is selected (no group) - zoom to just that mission, not the whole world
      if (missionBounds.length > 1) {
        map.fitBounds(L.latLngBounds(missionBounds), { padding: [60, 60], maxZoom: 8 });
      } else {
        map.setView(missionBounds[0], 8);
      }
    } else if (bounds.length > 1) {
      map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 6 });
    } else if (focusLatLng) {
      map.setView(focusLatLng, 5);
    } else if (selectedOpArea?.location?.lat && selectedOpArea?.location?.lng) {
      const lat = parseFloat(selectedOpArea.location.lat);
      const lng = parseFloat(selectedOpArea.location.lng);
      const zoom = parseFloat(selectedOpArea.location.zoom || "4") || 4;
      if (!isNaN(lat) && !isNaN(lng)) map.setView([lat, lng], zoom);
    } else if (isNew && dbBases.length > 0) {
      const lnglats = dbBases
        .map((b) => dmsToLatLng(b.base_coords))
        .filter(Boolean) as [number, number][];
      if (lnglats.length > 0) {
        map.fitBounds(
          L.latLngBounds(lnglats.map((ll) => L.latLng(ll[0], ll[1]))),
          { padding: [20, 20] }
        );
      }
    }

    setTimeout(() => map.invalidateSize(), 50);
  }, [mission, dbBases, groupedData, missionId, selectedOpArea, basesByIcao, selectedGroup, selectedPlatformInGroup, mapOpts, sroAreas]);

  const missionCount = groupedData?.missions?.length || 0;

  const statusChips = (m: {
    armed?: boolean;
    locked?: boolean;
    hold?: boolean;
    holdType?: string;
    canx?: boolean;
    lostLinkStatus?: boolean;
  }) => (
    <>
      {m.armed && (
        <Chip icon={<Bolt sx={{ fontSize: 14 }} />} label="Armed" size="small"
          sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(255,23,68,0.15)", color: "#ff1744" }} />
      )}
      {m.locked && (
        <Chip icon={<Lock sx={{ fontSize: 14 }} />} label="Locked" size="small"
          sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(247,43,239,0.15)", color: "#f72bef" }} />
      )}
      {m.hold && (
        <Chip icon={<Warning sx={{ fontSize: 14 }} />} label={`Hold: ${m.holdType || "N/A"}`} size="small"
          sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />
      )}
      {m.canx && (
        <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Cancelled" size="small"
          sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(158,158,158,0.15)", color: "#9e9e9e" }} />
      )}
      {m.lostLinkStatus && (
        <Chip label="Lost Link" size="small"
          sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(255,23,68,0.25)", color: "#ff5252" }} />
      )}
    </>
  );

  const renderMissionChip = (pm: ListMission) => {
    const isMissionSelected = pm._id === missionId;
    return (
      <Tooltip
        key={pm._id}
        title={`${pm.callsign} · ${pm.launchBase} · ${fmtZ(pm.onStation)}-${fmtZ(pm.offStation)} · ${pm.int_prime || ""}`}
      >
        <Chip
          label={pm.callsign}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            selectMission(pm._id);
          }}
          sx={{
            height: 20,
            fontSize: "0.65rem",
            backgroundColor: isMissionSelected ? "#f72bef" : "rgba(255,255,255,0.05)",
            color: isMissionSelected ? "#fff" : "#cacce5",
            cursor: "pointer",
            opacity: pm.canx ? 0.5 : 1,
            border: pm.hold ? "1px solid #ff9100" : undefined,

            "&:hover": {
              backgroundColor: isMissionSelected ? "#e025d8" : "rgba(255,255,255,0.15)",
            },
          }}
        />
      </Tooltip>
    );
  };

  const activeGroups =
    group === "PLATFORM" ? groupedData?.platforms :
    group === "INT" ? groupedData?.ints :
    group === "SENSOR" ? groupedData?.sensors :
    undefined;
  const groupLabel =
    group === "INT" ? "INTs" :
    group === "SENSOR" ? "Sensors" :
    group === "PLATFORM" ? "Platforms" :
    "Missions";

  const q = search.trim().toUpperCase();
  const matchesSearch = (m: ListMission) =>
    !q ||
    (m.callsign || "").toUpperCase().includes(q) ||
    (m.platform?.name || "").toUpperCase().includes(q) ||
    (m.platform?.designator || "").toUpperCase().includes(q) ||
    (m.launchBase || "").toUpperCase().includes(q);

  const timelineGroups: TimelineGroup[] = activeGroups
    ? Object.entries(activeGroups as Record<string, ListMission[]>).map(([name, list]) => ({
        name,
        color: groupColor(group, name, list as ListMission[]),
        missions: (list as ListMission[]).filter(matchesSearch),
      }))
    : [
        {
          name: "Missions",
          color: "#c42ebe",
          missions: (groupedData?.missions || []).filter(matchesSearch),
        },
      ];

  return (
    <Box sx={{ flex: 1, minHeight: 0, p: 0.5, display: "flex", flexDirection: "column", gap: 0.5, overflow: "hidden" }}>
      {/* ── Top Control Bar ───────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
          py: 0.5,
          backgroundColor: "#1a1c26",
          border: "1px solid #333",
          borderRadius: 1,
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="op-area-label" sx={{ color: "#8a90a0", fontSize: "0.85rem" }}>
            Operating Area
          </InputLabel>
          <Select
            labelId="op-area-label"
            value={operatingArea}
            label="Operating Area"
            onChange={(e) => setParam("operatingArea", e.target.value || null, true)}
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              fontSize: "0.85rem",
              "& .MuiSvgIcon-root": { color: "#8a90a0" },
            }}
          >
            <MenuItem value="">
              <em>All Areas</em>
            </MenuItem>
            {operatingAreasList.map((area) => (
              <MenuItem key={area} value={area}>
                {area}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="ATO Date"
          type="date"
          value={atoDate}
          InputLabelProps={{ shrink: true, sx: { color: "#8a90a0" } }}
          onChange={(e) => setParam("atoDate", e.target.value || null, true)}
          sx={{
            minWidth: 160,
            input: { color: "#fff", fontSize: "0.85rem" },
            label: { fontSize: "0.85rem" },
            ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="group-label" sx={{ color: "#8a90a0", fontSize: "0.85rem" }}>
            Group By
          </InputLabel>
          <Select
            labelId="group-label"
            value={group}
            label="Group By"
            onChange={(e) => setParam("group", e.target.value)}
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              fontSize: "0.85rem",
              "& .MuiSvgIcon-root": { color: "#8a90a0" },
            }}
          >
            <MenuItem value="PLATFORM">Platform</MenuItem>
            <MenuItem value="INT">INT</MenuItem>
            <MenuItem value="SENSOR">Sensors</MenuItem>
            <MenuItem value="NONE">None</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search callsign, platform, base…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "#8a90a0" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 220,
            input: { color: "#fff", fontSize: "0.85rem" },
            ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="sort-label" sx={{ color: "#8a90a0", fontSize: "0.85rem" }}>
            Sort by
          </InputLabel>
          <Select
            labelId="sort-label"
            value={sortBy}
            label="Sort by"
            onChange={(e) => setParam("sort", e.target.value)}
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              fontSize: "0.85rem",
              "& .MuiSvgIcon-root": { color: "#8a90a0" },
            }}
          >
            <MenuItem value="TAKEOFF">Takeoff</MenuItem>
            <MenuItem value="CALLSIGN">Callsign</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="startz-label" sx={{ color: "#8a90a0", fontSize: "0.85rem" }}>
            ATO Start
          </InputLabel>
          <Select
            labelId="startz-label"
            value={String(startHour)}
            label="ATO Start"
            onChange={(e) => setParam("startZ", e.target.value === "0" ? null : e.target.value)}
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              fontSize: "0.85rem",
              "& .MuiSvgIcon-root": { color: "#8a90a0" },
            }}
          >
            {[0, 6, 12, 18].map((h) => (
              <MenuItem key={h} value={String(h)}>{String(h).padStart(2, "0")}00Z</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_e, v) => { if (v) setParam("view", v); }}
          sx={{
            "& .MuiToggleButton-root": {
              color: "#8a90a0",
              borderColor: "#333",
              fontSize: "0.7rem",
              py: 0.5,
              "&.Mui-selected": { color: "#f72bef", backgroundColor: "rgba(247,43,239,0.1)" },
            },
          }}
        >
          <ToggleButton value="TIMELINE">
            <Timeline sx={{ fontSize: 16, mr: 0.5 }} /> Timeline
          </ToggleButton>
          <ToggleButton value="CARDS">Cards</ToggleButton>
        </ToggleButtonGroup>

        <Chip
          label={`${missionCount} mission${missionCount === 1 ? "" : "s"}`}
          size="small"
          sx={{ height: 28, backgroundColor: "rgba(0,184,255,0.12)", color: "#00b8ff", fontWeight: 600 }}
        />

        <Tooltip
          title={realtimeConnected ? "Live — connected to Aussidex realtime" : "Offline — updates require refresh"}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "default" }}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: realtimeConnected ? "#00e676" : "#555",
                boxShadow: realtimeConnected ? "0 0 6px rgba(0,230,118,0.7)" : "none",
              }}
            />
            <Typography sx={{ fontSize: "0.7rem", color: realtimeConnected ? "#00e676" : "#555", fontWeight: 600 }}>
              {realtimeConnected ? "LIVE" : "OFFLINE"}
            </Typography>
          </Box>
        </Tooltip>

        {atoHeader(atoDate) && (
          <Typography sx={{ ml: "auto", color: "#cacce5", fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.03em" }}>
            {atoHeader(atoDate)}
          </Typography>
        )}
      </Box>

      {/* ── Map-centric layout (IDEX-style): full-area operating picture
             with the timeline and detail floating as overlay panels ── */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderRadius: 1, overflow: "hidden", border: "1px solid #333" }}>
       {/* Map region: the map owns this whole area; only the detail column and small badges float on it */}
       <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        {/* Single always-mounted map (Leaflet needs a stable DOM node) */}
        <Box
          ref={mapContainerRef}
          sx={{
            position: "absolute", inset: 0, backgroundColor: "#12131b",
            "& .basemap-dark": { filter: MAP_THEME_FILTER.dark },
            "& .basemap-medium": { filter: MAP_THEME_FILTER.medium },
            "& .basemap-light": { filter: MAP_THEME_FILTER.light },
          }}
        />

        {/* Map badge + theme switcher (IDEX map settings: Dark/Medium/Light) */}
        <Box
          sx={{
            position: "absolute", right: 8, bottom: 8, zIndex: 1100,
            display: "flex", alignItems: "center", gap: 0.75,
            backgroundColor: "rgba(18,19,27,0.85)", border: "1px solid #333", borderRadius: 1, px: 1, py: 0.5,
          }}
        >
          <MapIcon sx={{ fontSize: 14, color: "#cacce5" }} />
          <Typography sx={{ color: "#cacce5", fontWeight: 600, fontSize: "0.7rem", mr: 0.5 }}>
            Operating Picture
          </Typography>
          {(["dark", "medium", "light"] as const).map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              onClick={() => setParam("mapTheme", t === "dark" ? null : t)}
              sx={{
                height: 18, fontSize: "0.6rem", cursor: "pointer", textTransform: "capitalize",
                backgroundColor: mapTheme === t ? "rgba(247,43,239,0.25)" : "transparent",
                color: mapTheme === t ? "#f72bef" : "#8a90a0",
              }}
            />
          ))}
          <IconButton size="small" onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
            <SettingsIcon sx={{ fontSize: 15, color: settingsAnchor ? "#f72bef" : "#8a90a0" }} />
          </IconButton>
        </Box>

        {/* Map display options popover (IDEX SETTINGS / DISPLAY OPTIONS / MAP LAYERS) */}
        <Popover
          open={!!settingsAnchor}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Box sx={{ p: 1.5, minWidth: 210, backgroundColor: "#1a1c26" }}>
            <Typography sx={{ color: "#f72bef", fontWeight: 700, fontSize: "0.7rem", mb: 0.5 }}>
              DISPLAY OPTIONS
            </Typography>
            <FormGroup>
              {([
                ["bases", "Bases"],
                ["otherMissions", "Filtered missions"],
                ["taskingRings", "Tasking rings"],
                ["targets", "Targets"],
                ["rings60", "60NM target rings"],
                ["sroBoundaries", "SRO Area Boundaries"],
              ] as const).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={mapOpts[key]}
                      onChange={(e) => setMapOpts((prev) => ({ ...prev, [key]: e.target.checked }))}
                      sx={{ py: 0.25, color: "#8a90a0", "&.Mui-checked": { color: "#f72bef" } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.72rem", color: "#cacce5" }}>{label}</Typography>}
                />
              ))}
            </FormGroup>
            {availableLayers.length > 0 && (
              <>
                <Typography sx={{ color: "#f72bef", fontWeight: 700, fontSize: "0.7rem", mt: 1, mb: 0.5 }}>
                  MAP LAYERS
                </Typography>
                <FormGroup>
                  {availableLayers.map((l) => (
                    <FormControlLabel
                      key={l._id}
                      control={
                        <Checkbox
                          size="small"
                          checked={activeLayers.includes(l._id)}
                          onChange={(e) =>
                            setActiveLayers((prev) =>
                              e.target.checked ? [...prev, l._id] : prev.filter((id) => id !== l._id)
                            )
                          }
                          sx={{ py: 0.25, color: "#8a90a0", "&.Mui-checked": { color: "#00b8ff" } }}
                        />
                      }
                      label={<Typography sx={{ fontSize: "0.72rem", color: "#cacce5" }}>{l.name}</Typography>}
                    />
                  ))}
                </FormGroup>
              </>
            )}
          </Box>
        </Popover>

        {/* ── Right-edge side panels (Collection Requirements / Log) ── */}
        <SidePanels atoDate={atoDate} />

        {/* ── Right column: mission detail (closable so the map is never blanketed) ── */}
        {(mission || loading || missionErr) && !detailOpen && (
          <Chip
            label={mission ? `Detail: ${mission.callsign}` : "Detail"}
            size="small"
            onClick={() => setDetailOpen(true)}
            sx={{
              position: "absolute", top: 8, right: 36, zIndex: 1100, cursor: "pointer",
              backgroundColor: "rgba(18,19,27,0.9)", color: "#f72bef", border: "1px solid #f72bef",
            }}
          />
        )}
        {(mission || loading || missionErr) && detailOpen && (
        <Box
          sx={{
            position: "absolute", top: 8, right: 36, bottom: 8,
            width: 400, maxWidth: "45%", zIndex: 1100,
            overflow: "auto",
            backgroundColor: "rgba(18,19,27,0.92)",
            backdropFilter: "blur(3px)",
            border: "1px solid #333",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <IconButton
            size="small"
            aria-label="Close detail"
            onClick={() => setDetailOpen(false)}
            sx={{ position: "absolute", top: 4, right: 4, color: "#8a90a0", zIndex: 1 }}
          >
            <Cancel sx={{ fontSize: 16 }} />
          </IconButton>
          {loading && (
            <LinearProgress
              sx={{ mb: 2, backgroundColor: "#333", "& .MuiLinearProgress-bar": { backgroundColor: "#f72bef" } }}
            />
          )}
          {missionErr && (
            <Paper
              elevation={0}
              sx={{ p: 4, textAlign: "center", backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 2 }}
            >
              <Typography sx={{ color: "#ff9100", fontSize: "1rem", mb: 1 }}>Mission Not Found</Typography>
              <Typography sx={{ color: "#8a90a0", fontSize: "0.8rem" }}>
                This mission id is not in the database. Pick a mission from the timeline or adjust filters.
              </Typography>
            </Paper>
          )}
          {mission && (
            <Box>
              {/* Status bar */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 2,
                  backgroundColor: "#1a1c26",
                  border: "1px solid #333",
                  borderRadius: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1rem", mr: 1 }}>
                  {mission.callsign}
                </Typography>
                {mission.primaryTaskingName && (
                  <Chip label={mission.primaryTaskingName} size="small"
                    sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(0,230,118,0.12)", color: "#00e676" }} />
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <FlightTakeoff sx={{ color: "#00b8ff", fontSize: 18 }} />
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem" }}>
                    <b>{mission._schedule.launchBase}</b>
                    {mission._schedule.landingBase &&
                      mission._schedule.landingBase !== mission._schedule.launchBase && (
                        <>
                          {" "}
                          → <b>{mission._schedule.landingBase}</b>
                        </>
                      )}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Radar sx={{ color: "#00e676", fontSize: 18 }} />
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem" }}>
                    On: <b>{fmtZ(mission._schedule.onStation)}</b>
                  </Typography>
                  <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>/ Off:</Typography>
                  <Typography sx={{ color: "#ff9100", fontSize: "0.75rem" }}>
                    <b>{fmtZ(mission._schedule.offStation)}</b>
                  </Typography>
                </Box>
                <Chip
                  icon={<Shield sx={{ fontSize: 14 }} />}
                  label={mission.operatingArea}
                  size="small"
                  sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(196,46,190,0.15)", color: "#f72bef" }}
                />
                <Chip
                  icon={<Lock sx={{ fontSize: 14 }} />}
                  label={mission.classification || "UNCLASSIFIED"}
                  size="small"
                  sx={{ height: 22, fontSize: "0.65rem", backgroundColor: "rgba(0,184,255,0.15)", color: "#00b8ff" }}
                />
                {statusChips(mission)}

                {user && (
                  <Box sx={{ ml: "auto", display: "flex", gap: 0.75 }}>
                    {!mission.canx && !mission.hold && (
                      <Button size="small" variant="outlined" color="warning" sx={{ fontSize: "0.65rem", py: 0.2 }}
                        onClick={() => openChange(HOLD_ACTION)}>
                        Hold
                      </Button>
                    )}
                    {mission.hold && (
                      <Button size="small" variant="outlined" color="success" sx={{ fontSize: "0.65rem", py: 0.2 }}
                        onClick={() => quickChange("return-to-fragged-task")}>
                        Return to Frag
                      </Button>
                    )}
                    {mission.canx && (
                      <Button size="small" variant="outlined" color="success" sx={{ fontSize: "0.65rem", py: 0.2 }}
                        onClick={() => quickChange("relaunch")}>
                        Relaunch
                      </Button>
                    )}
                    {!mission.canx && (
                      <>
                        <Button size="small" variant="outlined" sx={{ fontSize: "0.65rem", py: 0.2, color: "#00b8ff", borderColor: "#00b8ff" }}
                          onClick={() => openChange(DELAY_ACTION)}>
                          Delay
                        </Button>
                        <Button size="small" variant="outlined" color="error" sx={{ fontSize: "0.65rem", py: 0.2 }}
                          onClick={() => openChange(CANX_ACTION)}>
                          CANX
                        </Button>
                        <Button size="small" variant="outlined" sx={{ fontSize: "0.65rem", py: 0.2, color: "#cacce5", borderColor: "#555" }}
                          onClick={(e) => setMoreAnchor(e.currentTarget)}>
                          More ▾
                        </Button>
                        <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
                          {MORE_ACTIONS.map((a) => (
                            <MenuItem key={a.slug} dense sx={{ fontSize: "0.8rem" }} onClick={() => openChange(a)}>
                              {a.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </Box>
                )}
              </Paper>

              {changeLog.length > 0 && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 1 }}>
                  <Typography sx={{ color: "#f72bef", fontWeight: 700, fontSize: "0.8rem", mb: 1 }}>
                    Change Log
                  </Typography>
                  {changeLog.map((e, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "baseline", flexWrap: "wrap", mb: 0.5 }}>
                      <Chip label={e.type} size="small"
                        sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />
                      {e.reason && <Typography sx={{ color: "#cacce5", fontSize: "0.7rem" }}>{e.reason}</Typography>}
                      {e.justification && <Typography sx={{ color: "#8a90a0", fontSize: "0.7rem" }}>— {e.justification}</Typography>}
                      <Typography sx={{ color: "#555", fontSize: "0.65rem", ml: "auto" }}>
                        {e.author} · {fmtZLong(e.at)}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              )}

              <Grid container spacing={2}>
                {/* Mission info */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ color: "#f72bef", mb: 1.5, fontWeight: "bold", fontSize: "1rem" }}>
                      Mission
                    </Typography>
                    <Divider sx={{ borderColor: "#333", mb: 1.5 }} />
                    {mission.platform && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase" }}>
                          Platform
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: getPlatformColor(
                                mission.platform.designator || mission.platform.name
                              ),
                            }}
                          />
                          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>
                            {mission.platform.fullName || mission.platform.name}
                          </Typography>
                        </Box>
                        <Typography sx={{ color: "#8a90a0", fontSize: "0.7rem" }}>
                          {mission.platform.designator}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                          {mission.platform.int_disciplines?.map((d) => (
                            <Chip
                              key={d}
                              label={d}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: "0.55rem",
                                backgroundColor: `${INT_COLORS[d] || "#888"}22`,
                                color: INT_COLORS[d] || "#888",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {mission.sensors && mission.sensors.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <SensorsIcon sx={{ fontSize: 14, color: "#00b8ff" }} />
                          <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase" }}>
                            Sensors
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                          {mission.sensors.map((s, i) => (
                            <Chip
                              key={i}
                              label={`${s.sensor_short_name || s.sensor_name || "SNSR"}${
                                s.int_discipline ? ` · ${s.int_discipline}` : ""
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.6rem",
                                backgroundColor: "rgba(0,184,255,0.1)",
                                color: "#90caf9",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <List dense disablePadding>
                      {(
                        [
                          ["INT Prime", mission.int_prime],
                          ["INT Secondary", mission.int_secondary],
                          ["Frequency", mission.frequency],
                          ["ATO Day", mission.ato_day],
                          ["Mission #", mission.ato_mission_number],
                          ["Country", mission.country],
                          ["Primary Tasking", mission.primaryTaskingName],
                        ] as Array<[string, string | undefined]>
                      ).filter(([, value]) => !!value).map(([label, value]) => (
                        <ListItem key={label} disablePadding sx={{ py: 0.15 }}>
                          <ListItemText
                            primary={label}
                            secondary={value || "--"}
                            primaryTypographyProps={{ sx: { color: "#8a90a0", fontSize: "0.65rem" } }}
                            secondaryTypographyProps={{ sx: { color: "#cacce5", fontSize: "0.7rem" } }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {mission.COM_guidance && (
                      <Box sx={{ mt: 1.5, p: 1, backgroundColor: "rgba(255,145,0,0.08)", borderRadius: 1, border: "1px solid rgba(255,145,0,0.25)" }}>
                        <Typography sx={{ color: "#ff9100", fontSize: "0.65rem", textTransform: "uppercase", mb: 0.3 }}>
                          COM Guidance
                        </Typography>
                        <Typography sx={{ color: "#cacce5", fontSize: "0.72rem" }}>{mission.COM_guidance}</Typography>
                      </Box>
                    )}

                    {mission._userTags && mission._userTags.length > 0 && (
                      <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                        {mission._userTags.map((t) => (
                          <Chip key={t} label={t} size="small"
                            sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(247,43,239,0.12)", color: "#f72bef" }} />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>


                <Grid item xs={12}>
                  {/* Timeline */}
                  <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
                      <Timeline sx={{ fontSize: 16, color: "#00e676" }} />
                      <Typography variant="h6" sx={{ color: "#00e676", fontWeight: "bold", fontSize: "0.95rem" }}>
                        Timeline
                      </Typography>
                    </Box>
                    <Divider sx={{ borderColor: "#333", mb: 1.5 }} />
                    <Grid container spacing={1}>
                      {(
                        [
                          ["Takeoff", mission._schedule.takeoff || mission.events?.takeoff?.scheduled, mission._schedule.launchBase, <FlightTakeoff key="t" sx={{ fontSize: 16, color: "#00b8ff" }} />],
                          ["On Station", mission._schedule.onStation, null, <Radar key="o" sx={{ fontSize: 16, color: "#00e676" }} />],
                          ["Off Station", mission._schedule.offStation, null, <Radar key="f" sx={{ fontSize: 16, color: "#ff9100" }} />],
                          ["Landing", mission._schedule.landing || mission.events?.landing?.scheduled, mission._schedule.landingBase || mission.events?.landing?.base, <FlightLand key="l" sx={{ fontSize: 16, color: "#f72bef" }} />],
                        ] as Array<[string, string | null | undefined, string | null | undefined, React.ReactNode]>
                      ).map(([label, time, base, icon]) => (
                        <Grid item xs={6} sm={3} key={label}>
                          <Box sx={{ p: 1, backgroundColor: "rgba(255,255,255,0.03)", borderRadius: 1, border: "1px solid #2a2c38" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
                              {icon}
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>{label}</Typography>
                            </Box>
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.85rem" }}>
                              {fmtZLong(time)}
                            </Typography>
                            {base && (
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>{base}</Typography>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>

                  {/* Taskings (populated from mission.taskings) */}
                  {mission._taskings && mission._taskings.length > 0 && (
                    <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: "#ff9100", mb: 1, fontWeight: "bold", fontSize: "0.95rem" }}>
                        Taskings ({mission._taskings.length})
                      </Typography>
                      <Divider sx={{ borderColor: "#333", mb: 1 }} />
                      {mission._taskings.map((t, ti) => {
                        const start = periodStart(t);
                        const end = periodEnd(t);
                        return (
                          <Box key={t._id || ti} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, py: 0.4 }}>
                            <Chip label={t.type || "Tasking"} size="small"
                              sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />
                            {t.primary_int_discipline && (
                              <Chip label={t.primary_int_discipline} size="small"
                                sx={{ height: 18, fontSize: "0.6rem", backgroundColor: `${INT_COLORS[t.primary_int_discipline] || "#888"}22`, color: INT_COLORS[t.primary_int_discipline] || "#888" }} />
                            )}
                            {t.secondary_int_discipline && (
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>+ {t.secondary_int_discipline}</Typography>
                            )}
                            {(t as any).country && (
                              <Typography sx={{ color: "#cacce5", fontSize: "0.68rem" }}>{(t as any).country}</Typography>
                            )}
                            {t.supported_unit && (
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>for {t.supported_unit}</Typography>
                            )}
                            {(start || end) && (
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", ml: "auto" }}>
                                {fmtZ(start)}–{fmtZ(end)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Paper>
                  )}

                  {/* Tasks (Collection Decks) */}
                  <Paper elevation={0} sx={{ p: 2, backgroundColor: "#1a1c26", border: "1px solid #333", borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ color: "#00b8ff", mb: 1.5, fontWeight: "bold", fontSize: "1rem" }}>
                      Tasks — Collection Decks ({mission._collectionDecks?.length || 0})
                    </Typography>
                    <Divider sx={{ borderColor: "#333", mb: 1.5 }} />
                    {mission._collectionDecks && mission._collectionDecks.length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {mission._collectionDecks.map((deck, di) => {
                          // Group collection requirements by primary INT discipline
                          const groups: Record<string, typeof deck.collection_requirements> = {};
                          deck.collection_requirements.forEach((cr) => {
                            const key = cr.int_discipline || "UNKNOWN";
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(cr);
                          });
                          const groupKeys = Object.keys(groups);
                          return (
                            <Box
                              key={deck._id || di}
                              sx={{
                                border: "1px solid #333",
                                borderRadius: 1,
                                backgroundColor: "rgba(255,255,255,0.02)",
                                overflow: "hidden",
                              }}
                            >
                              {/* Deck header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                  backgroundColor: "rgba(0,184,255,0.06)",
                                  borderBottom: "1px solid #2a2c38",
                                }}
                              >
                                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>
                                  {deck.name || `Task ${di + 1}`}
                                </Typography>
                                <Chip
                                  label={deck.type}
                                  size="small"
                                  sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(196,46,190,0.15)", color: "#f72bef" }}
                                />
                                {deck.category === "NIB" && (
                                  <Chip
                                    label="NIB"
                                    size="small"
                                    title="Non-Interference Basis tasking"
                                    sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(255,145,0,0.18)", color: "#ff9100", fontWeight: 700 }}
                                  />
                                )}
                                {deck.category === "TASK" && (
                                  <Chip
                                    label="TASK"
                                    size="small"
                                    title="Regular tasking collection requirements"
                                    sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(0,184,255,0.15)", color: "#00b8ff", fontWeight: 700 }}
                                  />
                                )}

                                {deck.primary_int_discipline && (
                                  <Chip
                                    label={`PRI: ${deck.primary_int_discipline}`}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.6rem",
                                      backgroundColor: `${INT_COLORS[deck.primary_int_discipline] || "#888"}22`,
                                      color: INT_COLORS[deck.primary_int_discipline] || "#888",
                                    }}
                                  />
                                )}
                                {deck.secondary_int_discipline && (
                                  <Chip
                                    label={`SEC: ${deck.secondary_int_discipline}`}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "0.6rem",
                                      backgroundColor: `${INT_COLORS[deck.secondary_int_discipline] || "#888"}22`,
                                      color: INT_COLORS[deck.secondary_int_discipline] || "#888",
                                    }}
                                  />
                                )}
                                {deck.priority != null && (
                                  <Chip label={`P${deck.priority}`} size="small"
                                    sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />
                                )}
                                {deck.operation && (
                                  <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>{deck.operation}</Typography>
                                )}
                                {deck.allocated_area && (
                                  <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", ml: "auto" }}>
                                    {deck.allocated_area}
                                  </Typography>
                                )}
                              </Box>

                              {/* Grouped collection requirements */}
                              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {groupKeys.map((gk) => (
                                  <Box key={gk}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          backgroundColor: INT_COLORS[gk] || "#888",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          color: INT_COLORS[gk] || "#cacce5",
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        {gk}
                                      </Typography>
                                      <Typography sx={{ color: "#8a90a0", fontSize: "0.6rem" }}>
                                        ({groups[gk].length})
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 1.5 }}>
                                      {groups[gk].map((cr, ci) => (
                                        <Box
                                          key={cr._id || ci}
                                          sx={{
                                            borderLeft: `2px solid ${INT_COLORS[gk] || "#888"}`,
                                            pl: 1,
                                            py: 0.3,
                                          }}
                                        >
                                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                            {cr.type && (
                                              <Chip label={cr.type} size="small"
                                                sx={{ height: 16, fontSize: "0.55rem", backgroundColor: "rgba(255,255,255,0.06)", color: "#cacce5" }} />
                                            )}
                                            {cr.priority && (
                                              <Typography sx={{ color: "#ff9100", fontSize: "0.65rem", fontWeight: 700 }}>
                                                P{cr.priority}
                                              </Typography>
                                            )}
                                            {cr.jipcl_sensor && (
                                              <Typography sx={{ color: "#90caf9", fontSize: "0.65rem" }}>
                                                {cr.jipcl_sensor}
                                              </Typography>
                                            )}
                                            {cr.secondary && (
                                              <Chip label="SECONDARY" size="small"
                                                sx={{ height: 14, fontSize: "0.5rem", backgroundColor: "rgba(255,145,0,0.12)", color: "#ff9100" }} />
                                            )}
                                            {cr.supported_unit && (
                                              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>
                                                {cr.supported_unit}
                                              </Typography>
                                            )}
                                            {cr.region && (
                                              <Typography sx={{ color: "#8a90a0", fontSize: "0.6rem" }}>
                                                {cr.region}
                                                {cr.sub_region ? ` / ${cr.sub_region}` : ""}
                                              </Typography>
                                            )}
                                          </Box>
                                          {cr.exploitation_requirement?.eei && (
                                            <Typography sx={{ color: "#cacce5", fontSize: "0.68rem", mt: 0.2 }}>
                                              <b>EEI:</b> {cr.exploitation_requirement.eei}
                                            </Typography>
                                          )}
                                          {cr.exploitation_requirement?.amplification && (
                                            <Typography sx={{ color: "#8a90a0", fontSize: "0.62rem" }}>
                                              {cr.exploitation_requirement.amplification}
                                            </Typography>
                                          )}
                                          {cr.exploitation_requirement?.reportingCriteria && (
                                            <Typography sx={{ color: "#8a90a0", fontSize: "0.62rem" }}>
                                              Report: {cr.exploitation_requirement.reportingCriteria}
                                            </Typography>
                                          )}
                                          {cr.exploitation_requirement?.dissemination && (
                                            <Typography sx={{ color: "#8a90a0", fontSize: "0.62rem" }}>
                                              Dissem: {Array.from(new Set(String(cr.exploitation_requirement.dissemination).split(/,\s*/))).join(", ")}
                                            </Typography>
                                          )}
                                          {cr.targets && cr.targets.length > 0 && (
                                            <Box sx={{ mt: 0.5, pl: 0.5 }}>
                                              <Typography
                                                sx={{
                                                  color: "#8a90a0",
                                                  fontSize: "0.55rem",
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.04em",
                                                  mb: 0.3,
                                                }}
                                              >
                                                Targets ({cr.targets.length})
                                              </Typography>
                                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                                                {cr.targets.map((t, ti) => (
                                                  <Chip
                                                    key={t._id || ti}
                                                    label={
                                                      <Box
                                                        component="span"
                                                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                                      >
                                                        <span>{t.name || t.target_id || "Target"}</span>
                                                        {t.status && (
                                                          <span
                                                            style={{
                                                              fontSize: "0.5rem",
                                                              opacity: 0.8,
                                                              textTransform: "uppercase",
                                                            }}
                                                          >
                                                            · {t.status}
                                                          </span>
                                                        )}
                                                      </Box>
                                                    }
                                                    size="small"
                                                    sx={{
                                                      height: 16,
                                                      fontSize: "0.55rem",
                                                      backgroundColor: "rgba(0,230,118,0.1)",
                                                      color: "#00e676",
                                                    }}
                                                  />
                                                ))}
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ color: "#8a90a0", fontStyle: "italic", fontSize: "0.75rem" }}>
                        No tasks (collection decks) assigned to this mission.
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        )}
       </Box>

        {/* ── Bottom dock: ATO timeline (IDEX-style) or cards, collapsible ── */}
        <Box
          sx={{
            flexShrink: 0,
            height: timelineOpen ? (view === "TIMELINE" ? "38%" : 240) : 34,
            minHeight: timelineOpen ? 180 : 34,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "#12131b",
            borderTop: "1px solid #333",
            p: 1,
            pt: 0.5,
            transition: "height 200ms ease",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: timelineOpen ? 1 : 0 }}>
            <Typography variant="h6" sx={{ color: "#f72bef", fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
              {operatingArea && !operatingArea.includes(",")
                ? `${operatingArea} — ${groupLabel}`
                : `All Areas — ${groupLabel}`}
              {atoDate ? ` · ${atoDate}` : ""}
            </Typography>
            {/* Platform summary (IDEX right-rail style) */}
            {Object.entries(
              (groupedData?.missions || []).reduce<Record<string, number>>((acc, m) => {
                const key = m.platform?.name || "Unknown";
                acc[key] = (acc[key] || 0) + 1;
                return acc;
              }, {})
            ).map(([name, count]) => (
              <Chip
                key={name}
                label={`${count}x ${name}`}
                size="small"
                sx={{
                  height: 18, mr: 0.5,
                  fontSize: "0.62rem",
                  backgroundColor: "rgba(255,255,255,0.05)",
                  color: "#cacce5",
                  border: `1px solid ${getPlatformColor(
                    (groupedData?.missions || []).find((m) => (m.platform?.name || "Unknown") === name)?.platform?.designator || name
                  )}`,
                }}
              />
            ))}
            <Chip
              label={timelineOpen ? "Hide" : "Show"}
              size="small"
              onClick={() => setTimelineOpen((v) => !v)}
              sx={{ height: 20, fontSize: "0.65rem", cursor: "pointer", color: "#cacce5", backgroundColor: "rgba(255,255,255,0.06)" }}
            />
          </Box>

          {!timelineOpen ? null : view === "TIMELINE" ? (
            <AtoTimeline
              groups={timelineGroups}
              atoDate={atoDate}
              selectedId={missionId}
              onSelect={selectMission}
              sortBy={sortBy}
              startHour={startHour}
            />
          ) : activeGroups ? (
            activeGroups ? (
              Object.keys(activeGroups).length === 0 ? (
                <Paper elevation={0} sx={{ p: 2, backgroundColor: "#1a1c26", border: "1px solid #333" }}>
                  <Typography sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>
                    No missions for these filters.
                  </Typography>
                </Paper>

              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, overflow: "auto" }}>
                  {Object.entries(activeGroups as Record<string, ListMission[]>).map(([platName, platMissions]) => {
                    const list = platMissions as ListMission[];
                    const isSelected = selectedGroup ? selectedGroup === platName : list.some((m) => m._id === missionId);
                    const color = groupColor(group, platName, list);
                    return (
                      <Card
                        key={platName}
                        sx={{
                          backgroundColor: isSelected ? "rgba(247,43,239,0.1)" : "#1a1c26",
                          border: `1px solid ${isSelected ? "#f72bef" : "#333"}`,
                          borderRadius: 1,
                          cursor: "pointer",
                          flexShrink: 0,
                          "&:hover": {
                            borderColor: "#f72bef",
                            backgroundColor: "rgba(247,43,239,0.05)",
                          },
                        }}
                        onClick={() => {
                          setSelectedGroup((prev) => (prev === platName ? null : platName));
                          setSelectedPlatformInGroup(null);
                          const current = list.find((m) => m._id === missionId);
                          selectMission(current ? current._id : list[0]._id);
                        }}
                      >
                        <CardContent sx={{ py: 1.2, px: 1.5, "&:last-child": { pb: 1.2 } }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
                            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>
                              {platName}
                            </Typography>
                            <Chip
                              label={list.length}
                              size="small"
                              sx={{
                                ml: "auto",
                                height: 18,
                                fontSize: "0.65rem",
                                backgroundColor: "rgba(247,43,239,0.15)",
                                color: "#f72bef",
                              }}
                            />
                          </Box>
                          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {list.filter((m) => !selectedPlatformInGroup || (m.platform?.name || "Unknown") === selectedPlatformInGroup).map((pm) => renderMissionChip(pm))}                          {(selectedGroup === platName && group !== "PLATFORM") && (
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography sx={{ color: "#8a90a0", fontSize: "0.6rem", mr: 0.3 }}>Platforms:</Typography>
                              {Array.from(new Set(list.map((m) => m.platform?.name || "Unknown"))).map((p) => (
                                <Chip
                                  key={p}
                                  label={p}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPlatformInGroup((prev) => (prev === p ? null : p));
                                    const first = list.find((m) => (m.platform?.name || "Unknown") === p);
                                    if (first) selectMission(first._id);
                                  }}
                                  sx={{
                                    height: 18,
                                    fontSize: "0.6rem",
                                    cursor: "pointer",
                                    backgroundColor: selectedPlatformInGroup === p ? "rgba(0,184,255,0.25)" : "rgba(255,255,255,0.06)",
                                    color: selectedPlatformInGroup === p ? "#00b8ff" : "#cacce5",
                                    border: `1px solid ${selectedPlatformInGroup === p ? "#00b8ff" : "#333"}`,
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )
            ) : (
              <Typography sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>Loading...</Typography>
            )
          ) : groupedData?.missions ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, overflow: "auto" }}>
              {groupedData.missions.map((m) => {
                const isSelected = m._id === missionId;
                return (
                  <Card
                    key={m._id}
                    sx={{
                      backgroundColor: isSelected ? "rgba(247,43,239,0.1)" : "#1a1c26",
                      border: `1px solid ${isSelected ? "#f72bef" : "#333"}`,
                      borderRadius: 1,
                      cursor: "pointer",
                      flexShrink: 0,
                      "&:hover": {
                        borderColor: "#f72bef",
                        backgroundColor: "rgba(247,43,239,0.05)",
                      },
                    }}
                    onClick={() => selectMission(m._id)}
                  >
                    <CardContent sx={{ py: 1.2, px: 1.5, "&:last-child": { pb: 1.2 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: getPlatformColor(m.platform?.designator || m.platform?.name || ""),
                          }}
                        />
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.8rem" }}>
                          {m.callsign}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: "#8a90a0", fontSize: "0.7rem", mt: 0.5 }}>
                        {m.platform?.name || "Unknown"} · {m.launchBase} · {fmtZ(m.onStation)}-{fmtZ(m.offStation)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>Loading...</Typography>
          )}
        </Box>

      </Box>

      {/* ── Mission change dialog (config-driven, all change types) ── */}
      <Dialog open={!!changeDialog} onClose={() => setChangeDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {changeDialog?.slug === "cancel" ? "Cancel Mission (CANX)" : `${changeDialog?.label} — Mission Change`}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {changeErr && (
            <Typography sx={{ color: "#ff1744", fontSize: "0.8rem" }}>{changeErr}</Typography>
          )}
          {!changeDialog?.noReason && (
            <FormControl size="small">
              <InputLabel id="change-reason-label">Change Reason</InputLabel>
              <Select
                labelId="change-reason-label"
                value={changeReason}
                label="Change Reason"
                onChange={(e) => setChangeReason(e.target.value)}
              >
                <MenuItem value="WX">Weather (WX)</MenuItem>
                <MenuItem value="MX">Maintenance (MX)</MenuItem>
                <MenuItem value="Ops">Operations (Ops)</MenuItem>
                <MenuItem value="O">Other (O)</MenuItem>
              </Select>
            </FormControl>
          )}
          {(changeDialog?.fields || []).map((f) => (
            <TextField
              key={f.name}
              size="small"
              label={f.label}
              type={f.type === "datetime" ? "datetime-local" : "text"}
              value={changeFields[f.name] || ""}
              onChange={(e) => setChangeFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
              InputLabelProps={f.type === "datetime" ? { shrink: true } : undefined}
            />
          ))}
          <TextField
            size="small"
            label="Justification"
            multiline
            minRows={2}
            value={changeJustification}
            onChange={(e) => setChangeJustification(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeDialog(null)}>Close</Button>
          <Button variant="contained" onClick={submitChange} disabled={changeBusy}
            color={changeDialog?.slug === "cancel" ? "error" : "primary"}>
            {changeDialog?.slug === "cancel" ? "Confirm CANX" : `Apply ${changeDialog?.label}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}








